import {Effect} from '..';
import axios from 'axios';
import { buildBaseHeader, encodeContextObj, getAffiliateOid, isLocalStorageAvailable } from "../../util/helper-methods";
import {hashPassword, generateSalt} from '../../util/password';
import {AuthActions} from '.';
import {AppActions, AppEffects} from '../app';
import {Routes} from 'routes';
import {push} from 'connected-react-router';
import {
  BusinessSignUpFormData,
  CodeData, Context, FinishSignUpFormData,
  ForgotPasswordFormData,
  LoginFormData,
  PersonalSignUpFormData,
  ResetPasswordFormData
} from "../../util/interfaces";
import { AccountActions } from "../account";

export const startGetSession = (): Effect =>
  async (dispatch) => {
    try {
      const {data: session} = await axios.get('/auth/session');
      dispatch(AuthActions.successGetSession(session));
    } catch (err) {
      dispatch(AuthActions.failedGetSession());
    }
};

export const startLogin = ({email, password, gCaptchaResponse, captchaType}: LoginFormData): Effect =>
  async (dispatch, getState) => {
    try {
        dispatch(AuthActions.clearError(0));
        const rootState = getState();
        rootState.account.accountError && dispatch(AccountActions.clearAccountError());
        const headers = {...buildBaseHeader(rootState)};
        // if this is a merchant logging in and they have any apply or context that needs to be executed then need to
        // be added to the auth and deactivate url sent via email, these instructions are provided in context and authDest
        const authDest = rootState.app.urlExtension && 'context';
        let context;
        if(authDest === 'context') {
          const contextObj = {
            url: 'authenticate/awaiting' + rootState.app.urlExtension
          }
          context = encodeContextObj(contextObj);
        }
        await axios.post('/auth/login', {email, hashedPassword: await hashPassword(password), gCaptchaResponse, captchaType, authSource: 'centralAuth', authDest, context}, {headers});

      dispatch(AuthActions.successLogin());
      dispatch(AppActions.setUserInfo({email}));
      dispatch(AppActions.setIsLoading(true));
      await dispatch(startGetSession()); //refresh the session before handling completion
      dispatch(AppEffects.handleCompletion());

    } catch (err) {
      // in the case that a cloudflare challenge is sent, reload the page so the challenge can be completed
      if(err.response.data && typeof err.response.data === 'string' && err.response.data.includes(`</html>`)) {
        let url = window.location.href;
        if (url.includes('?')) {
          url += `&status=${err.response.status}`;
        } else {
          url += `?status=${err.response.status}`;
        }
        window.location.assign(url);
      } else if(err.response.data?.twoFactorPending){
          dispatch(AppActions.setUserInfo({email}));
          dispatch(startVerify2fa());
      } else if(err.response.data?.emailAuthenticationPending) {
          dispatch(AppActions.setUserInfo({email}));
          dispatch(AppActions.setIsBusinessLogin(true));
          dispatch(startVerifyEmail());
      } else {
        let rateLimit
          if(err.message === 'Request failed with status code 429') {
            rateLimit = 'Rate Limited';
          }
          dispatch(AuthActions.failedLogin(err.response.data?.message || err.response.data?.error || rateLimit || 'An unexpected error has occurred'));
      }
    }
};

export const startPersonalSignUp = ({givenName, familyName, email, password, agreedToTOSandPP, gCaptchaResponse, agreedToMarketingCommunications}: PersonalSignUpFormData): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      const headers = {...buildBaseHeader(getState())};

      await axios.post('/auth/register', {
        captchaType: 'invisible', givenName, familyName, email,
        hashedPassword: await hashPassword(password),
        salt: await generateSalt(), agreedToTOSandPP, gCaptchaResponse,
        optInEmailMarketing: agreedToMarketingCommunications,
        attribute: agreedToMarketingCommunications ? 'Personal Signup Webpage' : undefined,
      }, {headers});

      dispatch(AuthActions.successSignUp());
      dispatch(AppActions.setUserInfo({givenName, familyName, email}));
      await dispatch(startVerifyEmail());

    } catch (err) {
      dispatch(AuthActions.failedSignUp(err.response.data?.message || err.response.data?.error || 'An unexpected error occurred'));
    }
  };

export const startBusinessSignUp = ({
                                      organizationName,
                                      givenName,
                                      familyName,
                                      country,
                                      state,
                                      B2B,
                                      B2C,
                                      yearlyRevenue,
                                      email,
                                      password,
                                      agreedToTOSandPP,
                                      gCaptchaResponse,
                                      marketingOptInStatus,
                                      referrerId,
                                      employeeCount,
                                      industry,
                                      website,
                                      orgType
                                    }: BusinessSignUpFormData): Effect =>
  async (dispatch, getState) => {
    try {
      const rootState = getState();
      dispatch(AuthActions.clearError(0));
      rootState.account.accountError && dispatch(AccountActions.clearAccountError());
      const headers = {...buildBaseHeader(rootState)};
      const sessionData = {
        csrf: headers["x-csrf-token"],
        method: 'createSession',
        params: "{}",
      }

      //create session
      const response = await axios.post('/api/dashboard', sessionData, { headers });
      const sessionId = response.data.data

      let customerType = B2B ? 'B2B' : '';
      customerType += (B2B && B2C) ? '/' : '';
      customerType += B2C ? 'B2C' : '';

      const affiliateOid = getAffiliateOid();

      const createAccountParams = {
        enhancedOnboarding: true,

        affiliateOid,
        referrerId,
        agreedToTOSandPP,
        country,
        customerType: customerType,
        email,
        familyName,
        gCaptchaResponse,
        givenName,
        hashedPassword: await hashPassword(password),
        marketingOptInStatus: marketingOptInStatus ? 'Opted-in' : 'Opt-in Declined',
        name: organizationName,
        salt: await generateSalt(),
        sessionId,
        state,
        yearlyRevenue,
        captchaType: 'invisible',
        employeeCount,
        industry,
        website,
        orgType
      }

      const createAccountData = {
        csrf: headers["x-csrf-token"],
        method: 'createAccount',
        params: JSON.stringify(createAccountParams)
      }

      const response2 = await axios.post('/api/dashboard', createAccountData, { headers });
      if(response2.data.error) {
        throw new Error(response2?.data?.error);
      }

      dispatch(AuthActions.successSignUp());
      await dispatch(startGetSession()); //refresh the session prior to handling completion
      dispatch(AppEffects.handleCompletion());

    } catch (err) {
      dispatch(AuthActions.failedSignUp(err.message || err.response?.data?.message || err.response?.data?.error || 'An unexpected error occurred'));
    }

  };

export const startFinishSignup = ({
                                    givenName,
                                    familyName,
                                    organizationName,
                                    country,
                                    state,
                                    referrerId,
                                    B2B,
                                    B2C,
                                    yearlyRevenue,
                                    employeeCount,
                                    industry,
                                    orgType,
                                    website,
                                    agreedToTOSandPP,
                                    marketingOptInStatus,
                                    gCaptchaResponse
                                  }: FinishSignUpFormData): Effect =>
  async (dispatch, getState) => {
    try {
      const rootState = getState();
      dispatch(AuthActions.clearError(0));
      rootState.account.accountError && dispatch(AccountActions.clearAccountError());
      const headers = buildBaseHeader(rootState);

      const affiliateOid = getAffiliateOid();

      let customerType = B2B ? 'B2B' : '';
      customerType += (B2B && B2C) ? '/' : '';
      customerType += B2C ? 'B2C' : '';

      const params = {
        givenName,
        familyName,
        merchantName: organizationName,
        merchantCountry: country,
        merchantState: state,
        merchantEmployeeCount: employeeCount,
        merchantYearlyRevenue: yearlyRevenue,
        merchantCustomerType: customerType,
        merchantIndustry: industry,
        legalBusinessType: orgType,
        merchantWebsite: website,
        agreedToTOSandPP,
        marketingOptInStatus,
        gCaptchaResponse,
        captchaType: 'invisible',
        affiliateOid,
        referrerId,
        ...rootState.app.extendedRegistrationParams
      }

      //set the names of the merchant
      await axios.put(`/users/updateName`, {givenName, familyName}, {headers});
      await axios.post('/users/registerPaymentAcceptance', params, {headers});
      await dispatch(startGetSession());
      dispatch(AuthActions.successSignUp());
      dispatch(AppEffects.handleCompletion());
    } catch (err) {
      dispatch(AuthActions.failedSignUp(err.response.data?.message || err.response.data?.error || 'An unexpected error occurred'));
    }
  }

export const startLogout = (): Effect =>
  async (dispatch, getState) => {
    try {
      const rootState = getState();

      const headers = {
        ...buildBaseHeader(rootState)
      }

      await axios.post('/auth/logout', null, {headers});

      dispatch(AuthActions.successLogout());
      await dispatch(startGetSession()); // refresh the session so there isn't automatic redirect back to a dashboard
      return;

    } catch (err) {
      dispatch(AuthActions.failedLogout());
    }

  };

export const startVerifyEmail = (): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      const rootState = getState();
      let returnUrl: string;
      if(rootState.app.queryParams.dest === 'poynt') {
        const context: Context = {
          url: rootState.auth.session.poyntSignupUrl,
          queryParams: {
            email: encodeURI(rootState.app.userInfo.email || ''),
            name: encodeURI(rootState.app.userInfo.givenName + ' ' + rootState.app.userInfo.familyName)
          }
        }
        returnUrl = `/authenticate/awaiting?context=${encodeContextObj(context)}&dest=poynt`;
      } else {
        const context = {
          url: `authenticate/awaiting${rootState.app.urlExtension}`
        };
        returnUrl = '/authenticate/awaiting?apply=emailVerify&context=' + encodeContextObj(context);
      }

      const headers = {
        ...buildBaseHeader(rootState)
      }

      dispatch(AuthActions.setVerifyingEmail(true));

      if(!rootState.app.isBusinessLogin) {
        await axios.post('/auth/sendVerificationEmail', {returnUrl}, {headers});
        dispatch(AuthActions.sentVerifyEmail());
      }

    } catch (err) {
      dispatch(AuthActions.failedVerifyEmail());
    }
  }

// Used to verify email as part of personal sign up flow
export const verifyEmail = (key: string): Effect =>
  async (dispatch, getState) => {
    const rootState = getState();
    const headers = {
      ...buildBaseHeader(rootState)
    }
    try {
      dispatch(AuthActions.setVerifyingEmail(true));
      await axios.post('/auth/verifyEmailAddress', {payload: key}, {headers});
      window.location.assign('/authenticate/email-verification' + rootState.app.urlExtension)// push to verify success endpoint

    } catch (err) {
      if (err?.response?.data.message === 'User is already verified') {
        window.location.assign('/authenticate/email-verification' + rootState.app.urlExtension);
      } else {
        dispatch(AuthActions.failedVerifyEmail());
        await axios.post('/auth/logout', null, {headers});
        window.location.assign('/authenticate/email-verification/error')
      }
    }
  }

// Used to verify email as part of login process
export const verifyEmail2fa = (key: string): Effect =>
  async (dispatch, getState) => {
    const rootState = getState();
    const headers = {
      ...buildBaseHeader(rootState)
    }
    dispatch(AppActions.setIsLoading(true));
    try {
      await axios.post('/dashboard-api/verify-email-authentication', {emailAuthenticationToken: key}, {headers});
      dispatch(startGetSession());
      dispatch(push(Routes.SUCCESS_VERIFY_EMAIL + rootState.app.urlExtension)); // push to verify success endpoint
      dispatch(AppActions.setIsLoading(false));
    } catch (err) {
      dispatch(AuthActions.failedVerifyEmail());
      dispatch(push(Routes.FAILED_VERIFY_EMAIL + rootState.app.urlExtension));
      dispatch(AppActions.setIsLoading(false));
    }
  }

// Used to reject email auth as part of login process
export const rejectEmail2fa = (key: string): Effect =>
  async (dispatch, getState) => {
    const rootState = getState();
    const headers = {
      ...buildBaseHeader(rootState)
    }
    try {
      await axios.post('/dashboard-api/reject-email-authentication', {emailAuthenticationToken: key}, {headers});
      dispatch(push(Routes.FAILED_VERIFY_EMAIL + rootState.app.urlExtension));
      dispatch(AppActions.setIsLoading(false));
    } catch (err) {
      dispatch(AuthActions.failedVerifyEmail());
      dispatch(push(Routes.FAILED_VERIFY_EMAIL + rootState.app.urlExtension));
      dispatch(AppActions.setIsLoading(false));
    }
  }

export const startVerify2fa = (): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      dispatch(AuthActions.setVerifying2FA(true))

    } catch (err) {
      dispatch(AuthActions.failedVerify2fa());
    }
  }

export const send2faCode = ({code}:CodeData): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      const rootState = getState();
      const headers = {
        ...buildBaseHeader(rootState)
      };

      await axios.post("/auth/verify-two-factor", { twoFactorCode: code }, { headers });

      dispatch(AuthActions.successVerify2fa());
      dispatch(AuthActions.successLogin());
      dispatch(AppActions.setIsLoading(true));
      dispatch(AuthActions.setVerifying2FA(false));
      await dispatch(startGetSession()); //refresh the session before handling completion
      dispatch(AppEffects.handleCompletion());

    } catch (err) {
      dispatch(AuthActions.failedVerify2fa());
    }
  };

export const startForgotPassword = ({email, gCaptchaResponse}: ForgotPasswordFormData): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      dispatch(AuthActions.clearResetPasswordStatus());

      const rootState = getState();
      const headers = {
        ...buildBaseHeader(rootState)
      };

      const {data} = await axios.post('/password/reset', {captchaType: 'invisible', email, gCaptchaResponse, returnUrl: 'authenticate'}, {headers});

      if(data?.result && data.result.success === false){
        dispatch(AuthActions.failedForgotPassword(data.result.message))
      } else {
        dispatch(AuthActions.successForgotPassword());
      }
    } catch (err) {
      dispatch(AuthActions.failedForgotPassword('An unexpected error occurred'));
    }
  };

export const startResetPassword = ({password}: ResetPasswordFormData): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      const { resetKey } = getState().auth;

      const {data} = await axios.post('/password/change', {hashedPassword: await hashPassword(password), salt: await generateSalt(), resetKey})

      if(data.result && data.result.success) {
        dispatch(AuthActions.successResetPassword());
      } else {
        dispatch(AuthActions.failedResetPassword('An unexpected error occurred'));
      }

    } catch (err) {
      dispatch(AuthActions.failedResetPassword('An unexpected error occurred'));
    }
  }

export const disableResetPassword = (): Effect =>
  async (dispatch, getState) => {
    try {
      dispatch(AuthActions.clearError(0));
      const { resetKey } = getState().auth;

      const {data} = await axios.get(`/password/disableReset?resetKey=${resetKey}`)

      if(data.success) {
        dispatch(AuthActions.successDisableResetPassword());
      } else {
        dispatch(AuthActions.failedDisableResetPassword(data.error));
      }

    } catch (err) {
      dispatch(AuthActions.failedDisableResetPassword('An unexpected error occurred'));
    }
  }

export const applyAdaToken = (): Effect =>
  async (dispatch, getState) => {
    const rootState = getState();
    const adaToken = rootState.auth.session?.adaToken;
    const headers  = buildBaseHeader(rootState);

    try {
      const { data } = await axios.get(`/users/current`, {headers});
      if(adaToken) {
        const sensitiveMetaFields = {
          userName: data.name,
          customAccessToken: adaToken
        };
        if(isLocalStorageAvailable()) {
          localStorage.setItem('applyAdaToken', JSON.stringify(sensitiveMetaFields));
          localStorage.removeItem('applyAdaToken');
          const successContext = encodeContextObj({
            alertComponent: {
              heading: 'BitPay Virtual Assistant',
              subheading: 'Successfully Authenticated',
              icon: 'success',
              copy: 'Please return to the chat window and continue your conversation. You may close this tab.',
              support: false
            }
          });
          window.location.assign(`/authenticate/alert?context=${successContext}`);
        } else {
          const localStorageFailureContext = encodeContextObj({
            alertComponent: {
              heading: 'BitPay Virtual Assistant',
              subheading: 'Uh oh, something went wrong.',
              icon: 'warning',
              copy: 'Unable to communicate with BitPay virtual assistant. This is likely because browser storage is disabled.',
              support: true
            }
          });
          window.location.assign(`/authenticate/alert?context=${localStorageFailureContext}`);
        }
      }
    } catch(err) {
      const failureContext = encodeContextObj({
        alertComponent: {
          heading: 'BitPay Virtual Assistant',
          subheading: 'Uh oh, something went wrong.',
          icon: 'warning',
          copy: 'An error occurred authenticating your session with the BitPay virtual assistant.',
          support: true
        }
      });
      window.location.assign(`/authenticate/alert?context=${failureContext}`);
    }
  }
